/**
 * useConfirmationDialog hook
 *
 * Provides a promise-based API for displaying confirmation dialogs and alerts.
 * Returns methods for different dialog types and a DialogComponent that must be rendered.
 */

import { useState, useCallback, useMemo } from "react"
import type { ConfirmationDialogVariant } from "./ConfirmationDialog"
import { ConfirmationDialog } from "./ConfirmationDialog"

interface DialogState {
	open: boolean
	variant: ConfirmationDialogVariant
	title: string
	description: React.ReactNode
	confirmText: string
	cancelText: string
	showCancel: boolean
	resolve: ((value: boolean) => void) | null
	isLoading: boolean
}

/**
 * Hook for displaying confirmation dialogs and alerts with a promise-based API.
 *
 * @example Basic usage
 * ```tsx
 * function MyComponent() {
 *   const { showConfirm, DialogComponent } = useConfirmationDialog()
 *
 *   const handleDelete = async () => {
 *     const confirmed = await showConfirm("Are you sure?", "Confirm Delete")
 *     if (confirmed) {
 *       // Proceed with delete
 *     }
 *   }
 *
 *   return (
 *     <>
 *       {DialogComponent}
 *       <button onClick={handleDelete}>Delete</button>
 *     </>
 *   )
 * }
 * ```
 *
 * @example Different dialog types
 * ```tsx
 * const { showAlertInfo, showConfirmDelete, DialogComponent } = useConfirmationDialog()
 *
 * // Show info alert
 * await showAlertInfo("Please check your inputs", "Validation Error")
 *
 * // Show delete confirmation
 * const deleteConfirmed = await showConfirmDelete("This will permanently delete the item")
 * ```
 */
export function useConfirmationDialog() {
	const [dialogState, setDialogState] = useState<DialogState>({
		open: false,
		variant: "default",
		title: "",
		description: "",
		confirmText: "Confirm",
		cancelText: "Cancel",
		showCancel: true,
		resolve: null,
		isLoading: false,
	})

	const openDialog = useCallback(
		(
			variant: ConfirmationDialogVariant,
			description: React.ReactNode,
			title: string,
			confirmText: string,
			cancelText: string,
			showCancel: boolean
		): Promise<boolean> => {
			return new Promise((resolve) => {
				setDialogState({
					open: true,
					variant,
					title,
					description,
					confirmText,
					cancelText,
					showCancel,
					resolve,
					isLoading: false,
				})
			})
		},
		[]
	)

	/**
	 * Show a default alert dialog (no cancel button)
	 * @param description - The message to display
	 * @param title - The dialog title (default: "Alert")
	 * @param confirmText - The confirm button text (default: "OK")
	 * @returns Promise that resolves when OK is clicked
	 */
	const showAlert = useCallback(
		(description: React.ReactNode, title = "Alert", confirmText = "OK"): Promise<void> => {
			return openDialog("default", description, title, confirmText, "Cancel", false).then(() => undefined)
		},
		[openDialog]
	)

	/**
	 * Show an info alert dialog (no cancel button)
	 * @param description - The message to display
	 * @param title - The dialog title (default: "Information")
	 * @param confirmText - The confirm button text (default: "OK")
	 * @returns Promise that resolves when OK is clicked
	 */
	const showAlertInfo = useCallback(
		(description: React.ReactNode, title = "Information", confirmText = "OK"): Promise<void> => {
			return openDialog("info", description, title, confirmText, "Cancel", false).then(() => undefined)
		},
		[openDialog]
	)

	/**
	 * Show a default confirmation dialog (with cancel button)
	 * @param description - The message to display
	 * @param title - The dialog title (default: "Confirmation")
	 * @param confirmText - The confirm button text (default: "Confirm")
	 * @param cancelText - The cancel button text (default: "Cancel")
	 * @returns Promise that resolves to true if confirmed, false if cancelled
	 */
	const showConfirm = useCallback(
		(
			description: React.ReactNode,
			title = "Confirmation",
			confirmText = "Confirm",
			cancelText = "Cancel"
		): Promise<boolean> => {
			return openDialog("default", description, title, confirmText, cancelText, true)
		},
		[openDialog]
	)

	/**
	 * Show a destructive confirmation dialog for delete actions (with cancel button)
	 * @param description - The message to display
	 * @param title - The dialog title (default: "Delete Item")
	 * @param confirmText - The confirm button text (default: "Delete")
	 * @param cancelText - The cancel button text (default: "Cancel")
	 * @returns Promise that resolves to true if confirmed, false if cancelled
	 */
	const showConfirmDelete = useCallback(
		(
			description: React.ReactNode,
			title = "Delete Item",
			confirmText = "Delete",
			cancelText = "Cancel"
		): Promise<boolean> => {
			return openDialog("destructive", description, title, confirmText, cancelText, true)
		},
		[openDialog]
	)

	const handleConfirm = useCallback(() => {
		if (dialogState.resolve) {
			dialogState.resolve(true)
			setDialogState((prev) => ({ ...prev, open: false }))
		}
	}, [dialogState])

	const handleCancel = useCallback(() => {
		if (dialogState.resolve) {
			dialogState.resolve(false)
			setDialogState((prev) => ({ ...prev, open: false }))
		}
	}, [dialogState])

	const handleOpenChange = useCallback(
		(open: boolean) => {
			if (!open && dialogState.resolve) {
				// Dialog was closed by clicking outside or pressing ESC
				dialogState.resolve(false)
			}
			setDialogState((prev) => ({ ...prev, open }))
		},
		[dialogState]
	)

	const DialogComponent = useMemo(
		() => (
			<ConfirmationDialog
				open={dialogState.open}
				onOpenChange={handleOpenChange}
				variant={dialogState.variant}
				title={dialogState.title}
				description={dialogState.description}
				confirmText={dialogState.confirmText}
				cancelText={dialogState.cancelText}
				showCancel={dialogState.showCancel}
				onConfirm={handleConfirm}
				onCancel={handleCancel}
				isLoading={dialogState.isLoading}
			/>
		),
		[
			dialogState.open,
			dialogState.variant,
			dialogState.title,
			dialogState.description,
			dialogState.confirmText,
			dialogState.cancelText,
			dialogState.showCancel,
			dialogState.isLoading,
			handleOpenChange,
			handleConfirm,
			handleCancel,
		]
	)

	return {
		showAlert,
		showAlertInfo,
		showConfirm,
		showConfirmDelete,
		DialogComponent,
	}
}
